import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import GET_HWT_CLASSROOM_SETTINGS from 'graph/settings/hwt/get-hwt-classroom-settings-query';
import UPDATE_HWT_CLASSROOM_LANGUAGE_SETTING from 'graph/settings/hwt/update-hwt-classroom-language-setting-mutation';
import { updateCache } from 'graph/update-cache';
import Typography from '@material-ui/core/Typography';
import CommonLocaleSetting from '../common/Locale';
import { LocaleClassroomSettingProps } from '../../../../types';

const getClassroomHwtSettingsMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_HWT_CLASSROOM_SETTINGS,
      variables: {
        where: {
          classroomId
        }
      },
      reducer: (
        cachedData,
        { updateHwtClassroomSettingLanguage }
      ): { hwtClassroomSettings: any } => {
        const [{ value }] = updateHwtClassroomSettingLanguage;
        return {
          hwtClassroomSettings: {
            ...cachedData?.hwtClassroomSettings,
            locale: value
          }
        };
      }
    }
  ])
});

const LocaleSetting: FC<LocaleClassroomSettingProps> = props => {
  const { t } = useTranslation('HwtLocaleSetting');
  const [updateHwtClassroomSettingLanguage] = useMutation(
    UPDATE_HWT_CLASSROOM_LANGUAGE_SETTING,
    getClassroomHwtSettingsMutationCacheUpdate(+props.classroom.id)
  );

  const appLocaleSettingsProps = useMemo(
    () => ({
      description: t(
        'With this setting, student navigation and instruction will be displayed in the language chosen. By default, the language option is English.'
      ),
      formLabel: (
        <Typography variant="body2">
          <strong>{t('Note')}: </strong>
          {t(
            'To send assignments to students in their chosen language, the teacher language in the Interactive Digital Teaching Tool (IDTT) must match the student setting.'
          )}
        </Typography>
      ),
      mutation: updateHwtClassroomSettingLanguage
    }),
    [t, updateHwtClassroomSettingLanguage]
  );

  return <CommonLocaleSetting {...props} {...appLocaleSettingsProps} />;
};

export default LocaleSetting;
